import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

const alertVariants = cva(
  // Layout: a tinted callout surface. A grid that reserves a leading column for
  // an optional icon; when no icon is present the content spans the full width.
  // The (optional) leading `svg` is sized and tinted with the variant accent fg.
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border p-4 text-sm has-[>svg]:grid-cols-[1.25rem_1fr] has-[>svg]:gap-x-3 [&>svg]:size-5 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        info: 'bg-alert-info-bg text-alert-info-fg border-alert-info-border',
        success: 'bg-alert-success-bg text-alert-success-fg border-alert-success-border',
        warning: 'bg-alert-warning-bg text-alert-warning-fg border-alert-warning-border',
        destructive:
          'bg-alert-destructive-bg text-alert-destructive-fg border-alert-destructive-border',
        neutral: 'bg-alert-neutral-bg text-alert-neutral-fg border-alert-neutral-border',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm opacity-90 [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, alertVariants };
